<template>
	<layout-admin>
		<!-- Kopfzeile -->
		<header class="bg-white shadow">
			<div class="py-6 px-4 sm:px-6 lg:px-8 sm:flex sm:items-center sm:justify-between gap-x-4">
				<h1 class="text-3xl font-bold leading-tight text-gray-900">
					{{ __("Freight rates") }}
				</h1>
			</div>
		</header>

		<admin-table
			:rows="rows"
			:cols="cols"
			:loading="loading"
			:sort-columns="sort"
			:pagination="pagination"
			@sort-change="handleSortChange"
			@page-change="loadContent"
		>
			<template v-slot:row-actions="{ row }">
				<div class="flex items-center justify-end">
					<button
						class="inline-flex items-center justify-center text-orange-500 hover:bg-gray-200 rounded-lg p-2"
						:title="__('Edit freight rate')"
						@click="openEditModal(row)"
					>
						<font-awesome-icon :icon="['far', 'pen-to-square']" class="h-4 w-4" />
					</button>
				</div>
			</template>
		</admin-table>
	</layout-admin>
</template>

<script>
import { formatDate } from "../../utils/date";

export default {
	props: {
		locale: { type: String, required: true },
	},

	data() {
		return {
			loading: true,
			rows: [],
			pagination: { currentPage: 1, lastPage: 1, total: 0 },
			sort: [],
		};
	},

	computed: {
		cols() {
			return [
				{
					id: "id",
					name: this.__("ID"),
					thClass: "min-w-[28rem]",
					tdClass: "min-w-[28rem]",
				},
				{
					id: "distance_up_to",
					name: this.__("Distance up to (km)"),
					render: (v) => `${v} km`,
					thClass: "min-w-[28rem]",
					tdClass: "min-w-[28rem]",
				},
				{
					id: "cost_per_ton",
					name: this.__("Cost per ton (€/t)"),
					render: (v) => `${v} €`,
					thClass: "min-w-[28rem]",
					tdClass: "min-w-[28rem]",
				},
				{
					id: "updated_at",
					name: this.__("Last updated"),
					render: (v) => formatDate(v, { time: true, locale: this.locale }) ?? "—",
					thClass: "w-full text-right pr-10",
					tdClass: "text-right pr-10",
				},
			];
		},
	},

	mounted() {
		this.loadContent(1);
		this.$bus.$on("content:load", this.onContentLoad);
	},

	beforeDestroy() {
		this.$bus.$off("content:load", this.onContentLoad);
	},

	methods: {
		/**
		 * @return {void}
		 */
		onContentLoad() {
			this.loadContent(this.pagination.currentPage);
		},

		/**
		 * @param {number} page
		 * @return {void}
		 */
		loadContent(page = 1) {
			this.loading = true;

			axios
				.get(this.$route("admin.freight_rates.data"), {
					params: {
						page,
						per_page: 50,
						sort: JSON.stringify(this.sort),
					},
				})
				.then((response) => {
					this.rows                   = response.data.data;
					this.pagination.currentPage = response.data.current_page;
					this.pagination.lastPage    = response.data.last_page;
					this.pagination.total       = response.data.total;
				})
				.finally(() => {
					this.loading = false;
				});
		},

		/**
		 * @param {string} colId
		 * @return {void}
		 */
		handleSortChange(colId) {
			const idx = this.sort.findIndex((s) => s.column === colId);

			if (idx === -1) {
				this.sort.push({ column: colId, dir: "asc" });
			} else if (this.sort[idx].dir === "asc") {
				this.sort.splice(idx, 1, { column: colId, dir: "desc" });
			} else {
				this.sort.splice(idx, 1);
			}

			this.loadContent(1);
		},

		/**
		 * @param {Object} row
		 * @return {void}
		 */
		openEditModal(row) {
			this.$bus.$emit("modal:open", {
				view: { view: "views/Admin/FreightRateEdit", data: { row } },
				size: "sm",
			});
		},
	},
};
</script>
